import { For, Show, createEffect, createMemo, createSignal, on, onCleanup } from "solid-js";
import { callTiles, foldsChi } from "../game/calls";
import { buttonLive } from "../game/clock";
import { CONTROLS, openButtonOfKey } from "../game/keys";
import { PULSE, pulse } from "../game/haptics";
import type { Action } from "../pb/mj/record/v1/record_pb";
import { ActionType } from "../pb/mj/record/v1/record_pb";
import { t } from "../strings";
import { Tile } from "./Tile";

// Everything a prompt offers besides discarding: discards are taps on the hand. Calls show the tiles they
// would use, so two chi options are told apart and a pon or kan says what it takes without a look around
// the table.
const SHOWS_TILES = new Set([ActionType.CHI, ActionType.PON, ActionType.OPEN_KAN, ActionType.CLOSED_KAN, ActionType.ADDED_KAN]);

// A fixed order, so a button is always where the hand expects it: wins first, pass last.
const RANK = [ActionType.TSUMO, ActionType.RON, ActionType.DECLARE_RIICHI, ActionType.CLOSED_KAN, ActionType.ADDED_KAN,
  ActionType.OPEN_KAN, ActionType.PON, ActionType.CHI];
const order = (a: Action) => (a.type === ActionType.PASS ? 99 : RANK.indexOf(a.type) < 0 ? 50 : RANK.indexOf(a.type));

// Without assists (design 14 §6.6): every button always there, in its place, saying nothing of what the hand can do. A
// press with something behind it answers the prompt, choosing among the options when there are several; one with
// nothing behind it goes to the server, which rules on it.
const OPEN_BUTTONS: { key: string; types: ActionType[] }[] = [
  { key: "TSUMO", types: [ActionType.TSUMO] },
  { key: "RON", types: [ActionType.RON] },
  { key: "DECLARE_RIICHI", types: [ActionType.DECLARE_RIICHI] },
  { key: "OPEN_KAN", types: [ActionType.CLOSED_KAN, ActionType.ADDED_KAN, ActionType.OPEN_KAN] },
  { key: "PON", types: [ActionType.PON] },
  { key: "CHI", types: [ActionType.CHI] },
  { key: "PASS", types: [ActionType.PASS] },
];

/** The action a press stands for when nothing lies behind it: a kan is the seat's own on its turn. */
export function emptyPress(types: ActionType[], ownTurn: boolean): ActionType | null {
  if (types.includes(ActionType.PASS)) return null;
  if (types.includes(ActionType.OPEN_KAN)) return ownTurn ? ActionType.CLOSED_KAN : ActionType.OPEN_KAN;
  return types[0] ?? null;
}

/** How long a finger holds a button, or a hand its key, at a table without assists: a press with nothing behind it costs a hand, a finger is wider than
 *  the gap between the buttons, a key is easy to brush, and there is no taking one back. Long enough not to be a slip, short enough for a call's seconds.
 *  Skip costs nothing. */
export const HOLD_MS = 300;

export function OpenControls(props: { legal: Action[]; red: boolean; locked: boolean; ownTurn: boolean; callsOpen?: boolean; called?: number; onAct: (a: Action) => void; onPress: (type: ActionType) => void; keysOff?: () => boolean }) {
  const live = (types: readonly ActionType[]) => props.callsOpen === undefined || buttonLive(types, props.ownTurn, props.callsOpen);
  const [choosing, setChoosing] = createSignal<Action[] | null>(null);
  const [holding, setHolding] = createSignal("");
  const [tell, setTell] = createSignal("");
  let finger = false; // this press is a finger's: its click says nothing, the hold has said it
  let held = 0;
  let told = 0;
  const letGo = (key: string) => {
    if (!held) return;
    clearTimeout(held);
    held = 0;
    setHolding("");
    setTell(key);
    clearTimeout(told);
    told = window.setTimeout(() => setTell(""), 1400);
  };
  onCleanup(() => {
    clearTimeout(held);
    clearTimeout(told);
  });
  // The keys are the buttons, and hold as a finger does. A choice's options are 1, 2, 3.
  let keyed = "";
  const keyDown = (e: KeyboardEvent) => {
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || e.target instanceof HTMLInputElement || props.keysOff?.()) return;
    const key = e.key.toLowerCase();
    const options = choosing();
    if (options) {
      const a = options[Number(key) - 1];
      if (a) {
        e.preventDefault();
        props.onAct(a);
      }
      return;
    }
    const name = openButtonOfKey(key, props.ownTurn);
    const b = OPEN_BUTTONS.find((x) => x.key === name);
    if (!b || (e.target instanceof Element && e.target.closest("textarea, select") && e.target.closest(CONTROLS))) return;
    e.preventDefault();
    if (keyed || !live(b.types)) return;
    keyed = b.key;
    setTell("");
    setHolding(b.key);
    held = window.setTimeout(() => {
      held = 0;
      keyed = "";
      setHolding("");
      press(b.types);
    }, HOLD_MS);
  };
  const keyUp = (e: KeyboardEvent) => {
    const name = openButtonOfKey(e.key.toLowerCase(), props.ownTurn);
    if (!keyed || name !== keyed) return;
    letGo(keyed);
    keyed = "";
  };
  addEventListener("keydown", keyDown);
  addEventListener("keyup", keyUp);
  onCleanup(() => {
    removeEventListener("keydown", keyDown);
    removeEventListener("keyup", keyUp);
  });
  // A new offer closes the choice. A memo: left to on(), any update of the view at all would, a chi's choice closing under the finger.
  createEffect(on(createMemo(() => props.legal), () => setChoosing(null)));
  const press = (types: ActionType[]) => {
    if (!live(types)) return; // the hold outlasted the window
    const options = props.legal.filter((a) => types.includes(a.type));
    if (options.length > 0) {
      if (props.locked) return;
      if (options.length === 1) props.onAct(options[0]!);
      else setChoosing(options);
      return;
    }
    const empty = emptyPress(types, props.ownTurn);
    if (empty !== null) props.onPress(empty);
  };
  return (
    <div class="controls open-controls" role="group" aria-label="actions">
      <Show
        when={choosing()}
        fallback={
          <For each={OPEN_BUTTONS}>
            {(b) => (
              <button
                type="button"
                class={`action action-${b.key.toLowerCase()}${holding() === b.key ? " holding" : ""}`}
                disabled={!live(b.types)}
                onPointerDown={(e) => {
                  finger = e.pointerType !== "mouse" && b.key !== "PASS";
                  if (!finger) return;
                  setTell("");
                  setHolding(b.key);
                  held = window.setTimeout(() => {
                    held = 0;
                    setHolding("");
                    pulse(PULSE.land);
                    press(b.types);
                  }, HOLD_MS);
                }}
                onPointerUp={() => letGo(b.key)}
                onPointerCancel={() => letGo(b.key)}
                onPointerLeave={() => letGo(b.key)}
                onClick={() => {
                  if (finger) finger = false;
                  else press(b.types);
                }}
              >
                <span>{tell() === b.key ? t.holdToPress : (t.actions[b.key] ?? b.key)}</span>
              </button>
            )}
          </For>
        }
      >
        {(options) => (
          <For each={options()}>
            {(a, i) => (
              <button type="button" class={`action action-${ActionType[a.type]?.toLowerCase()}`} onClick={() => props.onAct(a)}>
                <span>{t.actions[ActionType[a.type] ?? ""] ?? ActionType[a.type]}</span>
                <span class="action-tiles">
                  <For each={callTiles(a, props.called)}>{(id) => <Tile id={id} red={props.red} size="s" highlight={id === props.called} />}</For>
                </span>
                <kbd class="action-key">{i() + 1}</kbd>
              </button>
            )}
          </For>
        )}
      </Show>
    </div>
  );
}

/** The hold, tried before it counts (the assessment's page): a tap that does nothing, met for the first time inside a two-second window in the game that
 *  sets a rating, was how it used to be learned (UX review 2026-11-02). */
export function HoldPractice() {
  const [state, setState] = createSignal<"idle" | "holding" | "short" | "done">("idle");
  let held = 0;
  let mouse = false;
  const start = () => {
    setState("holding");
    held = window.setTimeout(() => {
      held = 0;
      setState("done");
    }, HOLD_MS);
  };
  const stop = () => {
    if (!held) return;
    clearTimeout(held);
    held = 0;
    setState("short");
  };
  const keyDown = (e: KeyboardEvent) => {
    if (!e.repeat && e.key.toLowerCase() === "p" && state() !== "done") start();
  };
  const keyUp = (e: KeyboardEvent) => {
    if (e.key.toLowerCase() === "p") stop();
  };
  addEventListener("keydown", keyDown);
  addEventListener("keyup", keyUp);
  onCleanup(() => {
    clearTimeout(held);
    removeEventListener("keydown", keyDown);
    removeEventListener("keyup", keyUp);
  });
  const words = () => ({ idle: t.holdPracticeIdle, holding: t.holdPracticeIdle, short: t.holdPracticeShort, done: t.holdPracticeDone })[state()];
  return (
    <div class="hold-try">
      <p>{t.holdPracticeLead}</p>
      <button
        type="button"
        class={`hold-practice action secondary${state() === "holding" ? " holding" : ""}${state() === "done" ? " done" : ""}`}
        onPointerDown={(e) => {
          mouse = e.pointerType === "mouse";
          if (!mouse && state() !== "done") start();
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
        onClick={() => {
          if (mouse) setState("done");
        }}
      >
        <span aria-live="polite">{words()}</span>
      </button>
    </div>
  );
}

/** The key that presses a button (pages/TablePage), shown where a keyboard is likely. */
const KEY: Partial<Record<ActionType, string>> = {
  [ActionType.TSUMO]: "T",
  [ActionType.RON]: "R",
  [ActionType.DECLARE_RIICHI]: "R",
  [ActionType.CLOSED_KAN]: "K",
  [ActionType.ADDED_KAN]: "K",
  [ActionType.OPEN_KAN]: "K",
  [ActionType.PON]: "P",
  [ActionType.CHI]: "C",
  [ActionType.PASS]: "Esc",
};

export function Controls(props: { legal: Action[]; red: boolean; locked: boolean; called?: number; onAct: (a: Action) => void; keysOff?: () => boolean }) {
  const buttons = () => props.legal.filter((a) => a.type !== ActionType.DISCARD).sort((a, b) => order(a) - order(b));
  const chis = () => buttons().filter((a) => a.type === ActionType.CHI);
  // Several chi behind C: it asks which, by 1, 2, 3, where it used to take the first with no word (UX review 2026-11-02).
  const [choosing, setChoosing] = createSignal(false);
  createEffect(on(createMemo(() => props.legal), () => setChoosing(false)));
  const onKey = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.target instanceof HTMLInputElement || props.keysOff?.() || props.locked) return;
    const key = e.key.toLowerCase();
    if (choosing()) {
      const a = chis()[Number(key) - 1];
      if (a) {
        e.preventDefault();
        props.onAct(a);
      } else if (key === "escape") {
        e.preventDefault();
        e.stopPropagation();
        setChoosing(false);
      }
    } else if (key === "c" && chis().length > 1) {
      e.preventDefault();
      e.stopPropagation();
      setChoosing(true);
    }
  };
  addEventListener("keydown", onKey, true); // before the table's own keys
  onCleanup(() => removeEventListener("keydown", onKey, true));
  const folded = () => foldsChi(props.legal);
  const shown = () => (choosing() && folded() ? chis() : folded() ? buttons().filter((a, i, all) => a.type !== ActionType.CHI || all.findIndex((x) => x.type === ActionType.CHI) === i) : buttons());
  const chiNumber = (a: Action) => chis().indexOf(a) + 1;
  // The row is there with or without buttons (styles.css reserves its height upright): a prompt's buttons
  // appearing used to move the board and leave the hand under them (audit 2026-10-14).
  return (
    <div class="controls" role="group" aria-label="actions">
      <For each={shown()}>
        {(a) => {
          const fold = () => folded() && !choosing() && a.type === ActionType.CHI;
          return (
            <button type="button" class={`action action-${ActionType[a.type]?.toLowerCase()}`} disabled={props.locked} onClick={() => (fold() ? setChoosing(true) : props.onAct(a))}>
              <span>{t.actions[ActionType[a.type] ?? ""] ?? ActionType[a.type]}</span>
              <Show when={!fold() && SHOWS_TILES.has(a.type) && a.tiles.length > 0}>
                {/* In order, the tile taken lit: two small tiles alone did not say which was which. */}
                <span class="action-tiles">
                  <For each={callTiles(a, props.called)}>{(id) => <Tile id={id} red={props.red} size="s" highlight={id === props.called} />}</For>
                </span>
              </Show>
              <Show when={KEY[a.type]}>
                <kbd class="action-key">{choosing() && a.type === ActionType.CHI ? chiNumber(a) : KEY[a.type]}</kbd>
              </Show>
            </button>
          );
        }}
      </For>
      <Show when={choosing() && folded()}>
        <button type="button" class="secondary" onClick={() => setChoosing(false)}>
          {t.cancel}
        </button>
      </Show>
    </div>
  );
}
